import { useState } from "react";
import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { downloadCsv } from "@/lib/manufacturing/inventoryCsvExport";
import {
  laborRateImportCounts,
  laborRateImportLeftoverCsv,
  laborRateImportLeftoverRows,
  parseLaborRateImportText,
  type LaborRateImportExisting,
  type LaborRateImportRow,
} from "@/lib/manufacturing/laborCompanyRateImport";
import type { LaborRateRecipePick } from "@/lib/manufacturing/laborCompanyRateRecipe";
import { ManufacturingLaborCompanyRateImportLeftoverPrompt } from "./ManufacturingLaborCompanyRateImportLeftoverPrompt";
import { ManufacturingLaborCompanyRateImportReview } from "./ManufacturingLaborCompanyRateImportReview";

export function ManufacturingLaborCompanyRateImportModal({
  recipes,
  existing,
  busy,
  onSaveAccepted,
  onClose,
  onImported,
}: {
  recipes: LaborRateRecipePick[];
  existing: LaborRateImportExisting[];
  busy: boolean;
  onSaveAccepted: (
    rows: LaborRateImportRow[],
    opts?: { parkSuggestedLeftovers?: boolean }
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  onClose: () => void;
  onImported: () => void;
}) {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<LaborRateImportRow[] | null>(null);
  const [leftover, setLeftover] = useState<{
    count: number;
    csv: string;
    canImportRemaining: boolean;
  } | null>(null);

  function parse(source: string) {
    const out = parseLaborRateImportText(source, recipes, existing);
    if (!out.ok) {
      setError(out.error);
      setRows(null);
      return;
    }
    setError(null);
    setRows(out.rows);
  }

  function setStatus(id: string, status: LaborRateImportRow["status"], reason: string) {
    setRows((prev) =>
      prev ? prev.map((r) => (r.id === id ? { ...r, status, reason } : r)) : prev
    );
  }

  async function finish() {
    if (!rows) return;
    const saved = await onSaveAccepted(rows);
    if (!saved.ok) {
      setError(saved.error);
      return;
    }
    const counts = laborRateImportCounts(rows);
    if (counts.leftover > 0) {
      setLeftover({
        count: counts.leftover,
        csv: laborRateImportLeftoverCsv(rows),
        canImportRemaining: counts.canImportRemaining,
      });
      return;
    }
    onImported();
  }

  async function importRemaining() {
    if (!rows) return;
    const saved = await onSaveAccepted(rows, { parkSuggestedLeftovers: true });
    if (!saved.ok) {
      setError(saved.error);
      return;
    }
    const leftoverRows = laborRateImportLeftoverRows(rows, true);
    if (leftoverRows.length > 0) {
      setLeftover({
        count: leftoverRows.length,
        csv: laborRateImportLeftoverCsv(rows, true),
        canImportRemaining: false,
      });
      return;
    }
    onImported();
  }

  function downloadLeftovers() {
    if (!leftover) return;
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`labor-rates-leftover-${stamp}.csv`, leftover.csv);
    onImported();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 sm:items-center"
    >
      <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-t-2xl border border-gray-200 bg-white p-5 shadow-xl dark:border-zinc-700 dark:bg-zinc-900 sm:rounded-2xl">
        <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">Upload company labor rates</h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-zinc-400">
          Required columns: <code>sku</code> and <code>rate_usd</code>. Optional: <code>label</code>,{" "}
          <code>notes</code>, <code>pays_by</code>. Pays by is required before a rate can invoice — do not
          assume quantity 1. Unmatched and rejected rows with a rate are parked until you match a model
          and choose Pays by.
        </p>
        {error ? <p className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</p> : null}
        {leftover ? (
          <ManufacturingLaborCompanyRateImportLeftoverPrompt
            count={leftover.count}
            busy={busy}
            canImportRemaining={leftover.canImportRemaining}
            onImportRemaining={() => void importRemaining()}
            onDownload={downloadLeftovers}
            onSkip={onImported}
          />
        ) : rows ? (
          <div className="mt-4">
            <ManufacturingLaborCompanyRateImportReview
              rows={rows}
              busy={busy}
              onAccept={(id) => {
                const row = rows.find((r) => r.id === id);
                if (!row?.pays_by) {
                  setError("Choose Pays by before accepting.");
                  return;
                }
                setStatus(id, "accepted", "");
              }}
              onReject={(id) => setStatus(id, "rejected", "rejected")}
              onPaysBy={(id, paysBy) =>
                setRows((prev) =>
                  prev ? prev.map((r) => (r.id === id ? { ...r, pays_by: paysBy || null } : r)) : prev
                )
              }
              onPaysByAllSuggested={(paysBy) =>
                setRows((prev) =>
                  prev
                    ? prev.map((r) =>
                        r.status === "suggested" ? { ...r, pays_by: paysBy || null } : r
                      )
                    : prev
                )
              }
              onAcceptAll={() =>
                setRows((prev) =>
                  prev
                    ? prev.map((r) =>
                        r.status === "suggested" && r.pays_by ? { ...r, status: "accepted", reason: "" } : r
                      )
                    : prev
                )
              }
              onFinish={() => void finish()}
              onBack={() => setRows(null)}
            />
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <input
              type="file"
              accept=".csv,.txt,.json,text/csv,text/plain,application/json"
              className="block w-full text-sm text-gray-600 dark:text-zinc-300"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                void file.text().then(parse);
              }}
            />
            <textarea
              className={`${inputClass} min-h-[10rem] font-mono text-xs`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste CSV or JSON here…"
              spellCheck={false}
            />
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" className={ghostBtnClass} disabled={busy} onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className={mfgPrimaryBtnClass}
                disabled={busy || !text.trim()}
                onClick={() => parse(text)}
              >
                Match
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
